import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Menu, Package, Plus, Navigation, Search } from "lucide-react";

import { DataTable } from "@/components/ui/table";
import { getBaseNavigations } from "@/redux/features/baseNavigation/baseNavigationThunk";
import { getBaseNavigationColumns } from "../components/columns/BaseNavigationColumns";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import TablePagination from "@/components/common/TablePagination";
import { callLoader } from "@/redux/Loader/actions";
import AddBaseNavigationModal from "../modals/AddBaseNavigationModal";
import { getAccountTypeDropdownList } from "@/redux/features/accountType/accountTypeSlice";
import { SelectInput } from "../components/FormControls";

function BaseNavigationPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, baseNavigationlist, pagination } = useSelector(
    (state) => state.baseNavigations,
  );

  const accountTypes = useSelector((state) => state.accountType.accountTypes);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedNavigation, setSelectedNavigation] = useState("");
  const [selectedAccountType, setSelectedAccountType] = useState("");
  console.log("selectedAccountType: ", selectedAccountType);

  // ==========================================
  // HANDLERS
  // ==========================================

  const handleClose = (open) => {
    setModalOpen(open);

    if (!open) {
      setSelectedNavigation(null);
      setIsEdit(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedNavigation(null);
    setModalOpen(true);
  };

  const handleView = useCallback(
    (row) => {
      navigate(
        `/admin/base_navigations/${row.BaseNavigationId}/navigations?accountTypeId=${selectedAccountType}`,
      );
    },
    [navigate, selectedAccountType],
  );

  const handleEdit = useCallback((row) => {
    setIsEdit(true);
    setSelectedNavigation(row);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (row) => {
    try {
      console.log("Delete Navigation:", row);

      // Example:
      // await dispatch(
      //   deleteBaseNavigation(row.BaseNavigationId)
      // ).unwrap();

      // toast.success("Navigation deleted");
    } catch (error) {
      console.error(error);

      // toast.error("Failed to delete navigation");
    }
  }, []);

  // ==========================================
  // COLUMNS
  // ==========================================

  const columns = useMemo(
    () =>
      getBaseNavigationColumns({
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDelete,
      }),
    [handleView, handleEdit, handleDelete],
  );

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((col) => col.key),
  );

  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          visibleColumns.length === 0 || visibleColumns.includes(column.key),
      ),
    [columns, visibleColumns],
  );

  // ==========================================
  // FETCH DATA
  // ==========================================

  const fetchData = async () => {
    dispatch(callLoader(true));

    try {
      await dispatch(
        getBaseNavigations({
          iPageNo: currentPage,
          iPageRecords: pageSize,
          SearchKeyword: debouncedSearch,
          AccountTypeId: selectedAccountType,
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(callLoader(false));
    }
  };
  useEffect(() => {
    fetchData();
  }, [dispatch, currentPage, pageSize, debouncedSearch, selectedAccountType]);

  // ==========================================
  // SEARCH DEBOUNCE
  // ==========================================

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!accountTypes?.length) {
      dispatch(getAccountTypeDropdownList());
    }
  }, [dispatch, accountTypes?.length]);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
      <div className="space-y-4">
        {/* HEADER */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-white">
              <Navigation className="w-7 h-7 text-cyan-500" />
              Base Navigations
            </h1>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Manage navigation links, account type mappings, URLs, and display
              order
            </p>
          </div>

          <button
            onClick={handleAdd}
            className="
      flex items-center justify-center gap-2
      rounded-lg
      bg-teal-500 px-4 py-2
      text-white
      transition-colors
      hover:bg-teal-600
      w-full md:w-auto
    "
          >
            <Plus className="w-4 h-4" />
            Add Navigation
          </button>
        </div>
        {/* SEARCH & COLUMNS VISIBILITY */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* LEFT */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center flex-1">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                placeholder="Search navigation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="
          w-full
          rounded-lg
          border border-slate-200
          bg-white
          py-2.5
          pl-10
          pr-4
          text-slate-900
          placeholder-slate-400
          focus:outline-none
          focus:ring-2
          focus:ring-teal-500
          dark:border-slate-700
          dark:bg-slate-800
          dark:text-white
        "
              />
            </div>

            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="font-bold text-slate-900 dark:text-white">
                {pagination?.totalRecord ?? 0}
              </span>{" "}
              navigations found
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex gap-2">
            <SelectInput
              name="accountTypes"
              value={selectedAccountType}
              onValueChange={(name, value) => setSelectedAccountType(value)}
              placeholder="All Account Type"
              className="!py-2"
              options={accountTypes?.map((item) => ({
                value: item.VALUE,
                label: item.TEXT,
              }))}
            />
            <div className="flex justify-end">
              <ColumnVisibilityDropdown
                columns={columns}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                storageKey="base-navigations-table-columns"
              />
            </div>
          </div>
        </div>
        {/* TABLE */}
        <DataTable
          data={baseNavigationlist}
          columns={filteredColumns}
          loading={loading}
          emptyText="No Base Navigation found"
          Icon={Menu}
        />
        {/* PAGINATION */}
        <div className="p-4 pt-0">
          <TablePagination
            currentPage={currentPage}
            totalPages={pagination?.totalPage || 1}
            totalRecords={pagination?.totalRecord || 0}
            currentRecord={pagination?.currentRecord || 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
        {/* MODALS */}
        <AddBaseNavigationModal
          open={modalOpen}
          onOpenChange={handleClose}
          onSuccess={fetchData}
          isEdit={isEdit}
          editData={selectedNavigation}
        />
      </div>
    </div>
  );
}

export default BaseNavigationPage;
